import { AdminTeamView, User } from "../../Contracts/User";
import { create } from "zustand";
import { useRequestStore } from "../BackendStore";
import { MultiGetResponse } from "../../Contracts/Common";
import {
  AdminPortalDetails,
  AdminTeamAdyenEntityDetailsView,
  AdminTransferView
} from "../../Contracts/Admin";
import { useBannerStore } from "../Banner";
import { BannerType } from "../../Contracts/Banner";

const getTeamsUrl = "/v1/payments/admin/teams/";
const getOneTeamUrl = "/v1/payments/admin/teams/{team_id}/";
const getTeamAdyenEntityDetailsUrl = "/v1/payments/admin/teams/{team_id}/adyenEntityDetails/";
const getTeamTransfersListUrl = "/v1/payments/admin/teams/{team_id}/transfers/";
const getAdminPortalActionDetailsUrl = "/v1/payments/admin/teams/{team_id}/portal/details/";
const postAdminPortalActionDetailsUrl = "/v1/payments/admin/teams/{team_id}/portal/details/";
const manualPayoutOfTransfersUrl = "/v1/payments/admin/teams/<team_id>/manualPayout/";
const teamUsersUrl = "/v1/payments/admin/users/";
const enabledDebugUserAccountUrl = "/v1/payments/admin/teams/addOrEnableUser/";
const disableDebugUserAccountUrl = "/v1/payments/admin/teams/disableIntegrationUser/";

interface AdminTeamState {
  isLoading: boolean;
  teams: AdminTeamView[];
  teamUsers: User[];
  teamsCount?: number;
  teamDetails?: AdminTeamView;
  adminPortalDetails?: AdminPortalDetails;
  teamAdyenEntityDetails?: AdminTeamAdyenEntityDetailsView;
  getTeams: (
    getAccessTokenSilently: any,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    query?: string
  ) => Promise<void>;
  getOneTeam: (getAccessTokenSilently: any, team_id: string) => Promise<void>;
  getTeamAdyenEntityDetails: (getAccessTokenSilently: any, team_id: string) => Promise<void>;
  teamTransfers?: AdminTransferView;
  teamTransfersCount?: number;
  getTeamTransfers: (
    getAccessTokenSilently: any,
    team_id: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => Promise<void>;
  getAdminPortalActionDetails: (getAccessTokenSilently: any, team_id: string) => Promise<void>;
  editAdminPortalActionDetails: (
    getAccessTokenSilently: any,
    team_id: string,
    payload: Partial<AdminPortalDetails>
  ) => Promise<void>;
  manualPayoutOfTransfers: (
    getAccessTokenSilently: any,
    teamId: string,
    transferIds: number[],
    limit?: number,
    sort?: string,
    page?: number
  ) => Promise<void>;
  getTeamUsers: (
    getAccessTokenSilently: any,
    team_id: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => Promise<void>;
  enableDebugUserAccount: (getAccessTokenSilently: any, email: string) => Promise<string>;
  disableDebugUserAccount: (getAccessTokenSilently: any, email: string) => Promise<string>;
}

export const useAdminStore = create<AdminTeamState>(set => {
  const { getRequest, postRequest } = useRequestStore.getState();
  const { addBanner } = useBannerStore.getState();
  const getTeams = async (
    getAccessTokenSilently: any,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    query?: string
  ) => {
    set({ isLoading: true });
    try {
      let requestParams: {
        limit?: number;
        sort?: string;
        page?: number;
        include_total_count: boolean;
        filters?: string;
        q?: string;
      } = {
        limit,
        sort,
        page,
        include_total_count: true,
        filters,
        q: undefined
      };
      if (query && query.length > 3) {
        requestParams.q = query;
      }
      const response = await getRequest<MultiGetResponse<AdminTeamView>>(
        getAccessTokenSilently,
        getTeamsUrl,
        requestParams
      );
      set({
        teams: response.data,
        teamsCount: response.filtered_count_map?.all,
        isLoading: false
      });
    } catch (error) {
      console.error("Error fetching teams", error);
      set({ isLoading: false });
    }
  };

  const getOneTeam = async (getAccessTokenSilently: any, team_id: string) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<AdminTeamView>(
        getAccessTokenSilently,
        getOneTeamUrl.replace("{team_id}", team_id)
      );
      set({ teamDetails: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching teams", error);
      set({ isLoading: false });
    }
  };

  const getTeamAdyenEntityDetails = async (getAccessTokenSilently: any, team_id: string) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<AdminTeamAdyenEntityDetailsView>(
        getAccessTokenSilently,
        getTeamAdyenEntityDetailsUrl.replace("{team_id}", team_id)
      );
      set({ teamAdyenEntityDetails: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching teams", error);
      set({ isLoading: false });
    }
  };

  const getTeamTransfers = async (
    getAccessTokenSilently: any,
    team_id: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<AdminTransferView>(
        getAccessTokenSilently,
        getTeamTransfersListUrl.replace("{team_id}", team_id),
        { limit, sort, page, include_total_count: true }
      );
      set({
        teamTransfers: response,
        teamTransfersCount: response.transfers.filtered_count_map?.all,
        isLoading: false
      });
    } catch (error) {
      console.error("Error fetching teams", error);
      set({ isLoading: false });
    }
  };

  const getAdminPortalActionDetails = async (getAccessTokenSilently: any, team_id: string) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<AdminPortalDetails>(
        getAccessTokenSilently,
        getAdminPortalActionDetailsUrl.replace("{team_id}", team_id)
      );
      set({ adminPortalDetails: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching teams", error);
      set({ isLoading: false });
    }
  };

  const editAdminPortalActionDetails = async (
    getAccessTokenSilently: any,
    team_id: string,
    payload: Partial<AdminPortalDetails>
  ) => {
    set({ isLoading: true });
    try {
      const response = await postRequest<Partial<AdminPortalDetails>, AdminPortalDetails>(
        getAccessTokenSilently,
        postAdminPortalActionDetailsUrl.replace("{team_id}", team_id),
        payload
      );
      addBanner({
        type: BannerType.Success,
        message: "Details updated successfully"
      });
      set({ adminPortalDetails: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching teams", error);
      set({ isLoading: false });
    }
  };

  const manualPayoutOfTransfers = async (
    getAccessTokenSilently: any,
    teamId: string,
    transferIds: number[],
    limit?: number,
    sort?: string,
    page?: number
  ) => {
    set({ isLoading: true });
    try {
      const postRequestResponse = await postRequest<{ transfer_ids: number[] }, AdminTransferView>(
        getAccessTokenSilently,
        manualPayoutOfTransfersUrl.replace("<team_id>", teamId),
        { transfer_ids: transferIds }
      );
      if (postRequestResponse.message && postRequestResponse.message.length > 0) {
        addBanner({
          type: BannerType.Error,
          message: postRequestResponse.message
        });
      } else {
        addBanner({
          type: BannerType.Success,
          message: "Transfers paid out successfully"
        });
      }
      await getTeamTransfers(getAccessTokenSilently, teamId, limit, sort, page);
      set({ isLoading: false });
    } catch (error) {
      console.error("Error fetching teams", error);
      set({ isLoading: false });
    }
  };

  const getTeamUsers = async (
    getAccessTokenSilently: any,
    team_id: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<MultiGetResponse<User>>(
        getAccessTokenSilently,
        teamUsersUrl,
        {
          limit,
          sort,
          page,
          q: team_id
        }
      );
      set({ teamUsers: response.data, isLoading: false });
    } catch (error) {
      console.error("Error fetching teams", error);
      set({ isLoading: false });
    }
  };

  const enableDebugUserAccount = async (getAccessTokenSilently: any, email: string) => {
    set({ isLoading: true });
    try {
      const response = await postRequest<{ email: string }, { message: string }>(
        getAccessTokenSilently,
        enabledDebugUserAccountUrl,
        { email }
      );
      set({ isLoading: false });
      return response.message;
    } catch (error) {
      console.error("Error enabling debug user account", error);
      set({ isLoading: false });
      return "Error enabling debug user account";
    }
  };

  const disableDebugUserAccount = async (getAccessTokenSilently: any, email: string) => {
    set({ isLoading: true });
    try {
      const response = await postRequest<{ email: string }, { message: string }>(
        getAccessTokenSilently,
        disableDebugUserAccountUrl,
        { email }
      );
      set({ isLoading: false });
      return response.message;
    } catch (error) {
      console.error("Error disabling debug user account", error);
      set({ isLoading: false });
      return "Error disabling debug user account";
    }
  };

  return {
    isLoading: false,
    teams: [],
    teamUsers: [],
    getTeams,
    getOneTeam,
    getTeamAdyenEntityDetails,
    getTeamTransfers,
    getAdminPortalActionDetails,
    editAdminPortalActionDetails,
    manualPayoutOfTransfers,
    getTeamUsers,
    enableDebugUserAccount,
    disableDebugUserAccount
  };
});
